import {
  GetAllRolesResponse,
  getAllRolesService,
  loginService,
  logoutService,
} from "@/service/auth.service";
import { errorToast } from "@/utils/toast";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "@store";
import { NavigateFunction } from "react-router";
import { persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage"; // defaults to localStorage for web
import { resetAllState as resetClientState } from "./clientSlice";
import { resetAllState as resetProjectState } from "./projectSlice";
import { resetAllState as resetProjectTrackingState } from "./projectTrackingSlice";
import { resetAllState as resetTimeEntriesState } from "./timeEntriesSlice";
import { resetAllState as resetUserState } from "./userSlice";
import { resetQuickbookConnect, setIsConnected } from "./quickbookConnectSlice";
import { resetClientOrganizations } from "./clientOrganizationSlice";

interface User {
  id: string;
  token: string;
  username: string;
  role_code: string;
  expires_on: string;
  first_name: string;
  last_name: string;
  email: string;
  contact_number: string;
  name?: string;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
  roles: GetAllRolesResponse[];
}

const initialState: AuthState = {
  user: null,
  token: null,
  isAuthenticated: false,
  loading: false,
  error: null,
  roles: [],
};

// Async Actions
export const login = createAsyncThunk(
  "auth/login",
  async (credentials: { email: string; password: string }, { dispatch }) => {
    try {
      dispatch(setLoading(true));

      const { data }: any = await loginService(
        credentials.email,
        credentials.password
      );

      const user = {
        ...data.data,
        name: data.data.first_name + " " + data.data.last_name,
      };

      if (data.data.is_sync) {
        dispatch(setIsConnected(true));
      }

      dispatch(loginSuccess(user));

      const { data: roles } = await getAllRolesService();

      dispatch(setRoles(roles));

      return user;
    } catch (error: any) {
      console.log(error);
      const message = error.response?.data?.message || "Login failed";
      errorToast(message, "");
      dispatch(setError(message));
      throw error;
    }
  }
);

// Logout action
export const logout = createAsyncThunk(
  "auth/logout",
  async (navigate: NavigateFunction, { dispatch }) => {
    try {
      dispatch(setLoading(true));

      // Call the logout service
      await logoutService();

      // Dispatch logout success action which will trigger the rootReducer to clear all state
      dispatch(logoutSuccess());

      // Reset all state
      dispatch(resetClientState());
      dispatch(resetProjectState());
      dispatch(resetUserState());
      // dispatch(resetTimeEntryState());
      dispatch(resetTimeEntriesState());
      dispatch(resetProjectTrackingState());
      dispatch(resetQuickbookConnect());
      dispatch(resetClientOrganizations());

      // Force a redirect to sign in page
      setTimeout(() => {
        navigate("/auth/signin");
      }, 100);

      return true;
    } catch (error: any) {
      const message = error.response?.data?.message || "Logout failed";
      dispatch(setError(message));
      throw error;
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setRoles: (state, action: PayloadAction<GetAllRolesResponse[]>) => {
      state.roles = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.loading = false;
    },
    loginSuccess: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      state.token = action.payload.token;
      state.isAuthenticated = true;
      state.loading = false;
      state.error = null;
    },
    logoutSuccess: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.loading = false;
      state.error = null;
    },
    updateDisplayName: (
      state,
      action: PayloadAction<{ first_name: string; last_name: string }>
    ) => {
      state.user = {
        ...state.user,
        name: action.payload.first_name + " " + action.payload.last_name,
      } as User;
    },
  },
});

export const {
  setLoading,
  setError,
  loginSuccess,
  logoutSuccess,
  setRoles,
  updateDisplayName,
} = authSlice.actions;

// Selectors
export const selectAuth = (state: RootState) => state.auth;
export const selectUser = (state: RootState) => state.auth.user;
export const selectIsAuthenticated = (state: RootState) =>
  state.auth.isAuthenticated;
export const selectAuthLoading = (state: RootState) => state.auth.loading;
export const selectAuthError = (state: RootState) => state.auth.error;
export const selectToken = (state: RootState) => state.auth.token;
export const selectUserRole = (state: RootState) => state.auth.user?.role_code;

const persistConfig = {
  key: "auth",
  storage,
  whitelist: ["token", "isAuthenticated", "user"], // only persist these fields
};

export default persistReducer(persistConfig, authSlice.reducer);
